import { useTokenStore } from "@/common/providers/token-provider";

// Market AI backend runs separately (see backend/). Default to localhost for local dev.
// Set VITE_MARKET_AI_API_URL in .env to point to a deployed backend.
const BASE_URL: string =
  (import.meta.env.VITE_MARKET_AI_API_URL as string | undefined) ??
  "http://localhost:8000";

type ChartDataPoint = {
  datetime: string;
  rate: number;
};

type ChartDataset = {
  label: string;
  data: ChartDataPoint[];
};

type Visualization = {
  type: "line" | "bar";
  title: string;
  datasets: ChartDataset[];
};

type ChatRequest = {
  message: string;
  session_id?: string;
};

type ChatResponse = {
  answer: string;
  visualization: Visualization | null;
  sources: string[];
};

type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  visualization?: Visualization | null;
  sources?: string[];
};

const sendChatMessage = async (
  req: ChatRequest,
  onStatus: (text: string) => void,
): Promise<ChatResponse> => {
  const token = useTokenStore.getState().token;
  const response = await fetch(`${BASE_URL}/api/market/chat`, {
    body: JSON.stringify(req),
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    method: "POST",
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => response.statusText);
    throw new Error(detail);
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  for (
    let chunk = await reader.read();
    !chunk.done;
    chunk = await reader.read()
  ) {
    const { value } = chunk;

    buffer += decoder.decode(value as Uint8Array, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";

    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const event = JSON.parse(line.slice(6)) as {
        type: string;
        text?: string;
        data?: ChatResponse;
      };
      if (event.type === "status" && event.text) {
        onStatus(event.text);
      } else if (event.type === "result" && event.data) {
        return event.data;
      } else if (event.type === "error") {
        throw new Error(event.text ?? "Unknown error");
      }
    }
  }

  throw new Error("Stream ended without result");
};

export { sendChatMessage };
export type {
  ChartDataPoint,
  ChartDataset,
  ChatMessage,
  ChatRequest,
  ChatResponse,
  Visualization,
};
